import React, { useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { ArrowLeftOutlined, ArrowRightOutlined, WarningOutlined } from "@ant-design/icons";
import { CircularProgress } from "@material-ui/core";
import styles from "./SwmsWizard.module.css";

export default function SwmsWizardStep3({
  selectedCategories,
  selectedMethods,
  methodsByCategory,
  selectedAreas,
  expandedQLIs,
  catLabels,
  showValidation,
  submitting,
  onToggleQLIExpand,
  onToggleArea,
  onToggleQLIAllAreas,
  onContinue,
  onBack,
  onCancel
}) {
  const parentTasks = useSelector((state) => state.Job.parentTasks);
  const getAreaSelectionKey = (qliId, area) => `${qliId}::${area}`;
  const qlis = Array.isArray(parentTasks) && parentTasks.length > 0
    ? parentTasks.map((task) => ({
        id: task.task_id,
        title: task.qli_task_name,
        areas: Array.isArray(task.task_areas) ? task.task_areas.map((a) => a.area_name) : []
      }))
    : [];

  const qliCheckboxRefs = useRef({});
  const methodLabelsById = Object.values(methodsByCategory || {})
    .flat()
    .reduce((labels, method) => {
      if (method && method.id !== undefined) {
        labels[Number(method.id)] = method.method_name || "";
      }
      return labels;
    }, {});

  // Update indeterminate state for QLI checkboxes
  useEffect(() => {
    qlis.forEach((qli) => {
      if (qliCheckboxRefs.current[qli.id]) {
        const selectedAreasInQLI = qli.areas.filter((area) => selectedAreas.includes(getAreaSelectionKey(qli.id, area)));
        const someAreasSelected = selectedAreasInQLI.length > 0 && selectedAreasInQLI.length < qli.areas.length;
        qliCheckboxRefs.current[qli.id].indeterminate = someAreasSelected;
      }
    });
  }, [selectedAreas, qlis]);

  return (
    <div className={styles.stepBody}>
      {/* Recap banner */}
      <div className={styles.recapBanner}>
        <div className={styles.recapColumn}>
          <span className={styles.recapLabel}>Selected Categories:</span>
          <div className={styles.recapTags}>
            {selectedCategories.map((catId) => (
              <span key={catId} className={styles.recapTag}>
                {catLabels[catId]}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.recapColumn}>
          <span className={styles.recapLabel}>Selected Methods:</span>
          <div className={styles.recapTags}>
            {selectedMethods.slice(0, 3).map((methodId) => (
              <span key={methodId} className={styles.recapTag}>
                {methodLabelsById[Number(methodId)] || methodId}
              </span>
            ))}
            {selectedMethods.length > 3 && (
              <span className={styles.recapTag}>
                +{selectedMethods.length - 3} more
              </span>
            )}
          </div>
        </div>
      </div>

      <h1 className={styles.stepTitle}>Step 3: Select Quote Line Items</h1>
      <p className={styles.stepSubtitle}>Link this SWMS to specific Quote Line Items and the areas they cover.</p>

      <div className={styles.qlisContainer}>
        <h3 className={styles.qlisTitle}>Job Items & Areas</h3>
        <p className={styles.qlisHint}>Select the Quote Line Items and the specific Areas that this SWMS covers.</p>

        <div className={styles.qlisList}>
          {qlis.map((qli) => {
            const isExpanded = expandedQLIs.includes(qli.id);
            const selectedAreasInQLI = qli.areas.filter((area) => selectedAreas.includes(getAreaSelectionKey(qli.id, area)));
            const allAreasSelected = selectedAreasInQLI.length === qli.areas.length;

            return (
              <div key={qli.id} className={styles.qliItem}>
                <div
                  className={styles.qliHeader}
                  onClick={() => {
                    if (!submitting) {
                      onToggleQLIExpand(qli.id);
                    }
                  }}
                >
                  <input
                    ref={(el) => {
                      if (el) qliCheckboxRefs.current[qli.id] = el;
                    }}
                    type="checkbox"
                    className={styles.qliCheckbox}
                    checked={allAreasSelected}
                    disabled={submitting}
                    onChange={(e) => onToggleQLIAllAreas(qli.id, e)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={styles.qliTitle}>{qli.title}</span>
                  <span className={`${styles.qliChevron} ${isExpanded ? styles.expanded : ""}`}>
                    ▼
                  </span>
                </div>

                {isExpanded && (
                  <div className={styles.qliAreas}>
                    {qli.areas.map((area) => {
                      const isAreaSelected = selectedAreas.includes(getAreaSelectionKey(qli.id, area));
                      return (
                        <label key={`${qli.id}-${area}`} className={styles.areaLabel}>
                          <input
                            type="checkbox"
                            className={styles.areaCheckbox}
                            checked={isAreaSelected}
                            disabled={submitting}
                            onChange={() => onToggleArea(qli.id, area)}
                          />
                          <span>{area}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {showValidation && (
          <div className={styles.validationMsg}>
            <WarningOutlined />
            Please select at least one area to continue.
          </div>
        )}
      </div>

      <div className={styles.stepFooterBetween}>
        <button className={styles.backButton} onClick={onBack} disabled={submitting}>
          <ArrowLeftOutlined />
          Back to Methods
        </button>
        <div className={styles.footerRightGroup}>
          {submitting && <CircularProgress size={24} />}
          <button className={styles.cancelButton} onClick={onCancel}>
            Cancel
          </button>
          <button className={styles.continueButton} onClick={onContinue} disabled={submitting}>
            Create SWMS
            <ArrowRightOutlined />
          </button>
        </div>
      </div>
    </div>
  );
}
